import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, fmtErr } from "../../lib/api";
import { Anchor, CheckCircle2, ShieldCheck } from "lucide-react";
import { Button } from "../../components/ui/button";

export default function AccessApprove() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!token) {
      setErr("Missing approval token.");
      setLoading(false);
      return;
    }
    api
      .get(`/access-requests/admin/info?token=${encodeURIComponent(token)}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setErr(fmtErr(e)))
      .finally(() => setLoading(false));
  }, [token]);

  const approve = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.post("/access-requests/admin/approve", { token });
      setDone(r.data);
    } catch (e) {
      setErr(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-zinc-50">
      <div
        className="w-full max-w-md bg-white border border-zinc-200 rounded-lg p-8 space-y-6"
        data-testid="access-approve-page"
      >
        <Link to="/" className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-ink text-white grid place-items-center">
            <Anchor className="h-4 w-4" />
          </div>
          <span className="font-display text-[17px] font-medium">Seavanto · Admin</span>
        </Link>

        {loading && <div className="text-sm text-zinc-600">Loading request…</div>}

        {!loading && err && !done && (
          <div
            className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800"
            data-testid="access-approve-error"
          >
            {err}
          </div>
        )}

        {!loading && info && !done && (
          <>
            <div>
              <ShieldCheck className="h-6 w-6 text-ink mb-3" />
              <h1 className="font-display text-2xl font-medium tracking-tight">Approve access request</h1>
              <p className="text-sm text-zinc-600 mt-1">
                Approving this request creates an invite-placeholder user and emails them a
                link to set their password.
              </p>
            </div>

            <dl className="rounded-md border border-zinc-200 divide-y divide-zinc-200 text-sm">
              <div className="flex justify-between p-3">
                <dt className="text-zinc-500 uppercase tracking-wider text-xs">Name</dt>
                <dd className="text-ink" data-testid="approve-name">{info.name || "—"}</dd>
              </div>
              <div className="flex justify-between p-3">
                <dt className="text-zinc-500 uppercase tracking-wider text-xs">Email</dt>
                <dd className="text-ink" data-testid="approve-email">{info.email}</dd>
              </div>
              {info.company && (
                <div className="flex justify-between p-3">
                  <dt className="text-zinc-500 uppercase tracking-wider text-xs">Company</dt>
                  <dd className="text-ink" data-testid="approve-company">{info.company}</dd>
                </div>
              )}
            </dl>

            <Button
              onClick={approve}
              disabled={busy}
              className="w-full bg-ink hover:bg-black"
              data-testid="access-approve-submit"
            >
              {busy ? "Approving…" : "Approve and send invite"}
            </Button>
          </>
        )}

        {done && (
          <div
            className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800"
            data-testid="access-approve-done"
          >
            <div className="flex items-center gap-2 font-medium">
              <CheckCircle2 className="h-4 w-4" /> Approved
            </div>
            <div className="mt-2">
              We've emailed <span className="font-medium">{done.email}</span> a link to set their password.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
